import { PostMessage, PostMessagePayload, PostMessageType } from '@abiglobalhealth/go-types'
import { useCallback, useContext, useEffect, useRef } from 'react'

import { trackEvent } from '@/api/events'
import { NavigatorContext } from '@/contexts/navigator'
import { useUser } from '@/hooks/useUser'
import { isAbortError } from '@/utils/errors'

export const usePostMessage = () => {
  const { navigator } = useContext(NavigatorContext)
  const isInIframe = useRef(false)
  const { user } = useUser()
  const abortControllersRef = useRef<Set<AbortController>>(new Set())

  const sendPostMessage = useCallback(
    async (type: PostMessageType, payload?: PostMessagePayload) => {
      const message: PostMessage = { type, payload } as PostMessage

      const abortController = new AbortController()
      abortControllersRef.current.add(abortController)

      console.log('PostMessage - Tracking event', payload, { user, navigator }, { message })
      await trackEvent({ userId: user?.data?.userId!, event: message }, abortController.signal)
        .catch((error) => {
          if (isAbortError(error)) {
            console.log('PostMessage - Request aborted')
            return
          }
          console.error('PostMessage - Error tracking event', error)
        })
        .finally(() => {
          abortControllersRef.current.delete(abortController)
        })

      if (!isInIframe.current) return

      console.log('PostMessage - Sending message to parent', message)
      try {
        window.parent.postMessage(message, '*')
      } catch (error) {
        console.error('PostMessage - Error sending message to parent', error)
      }
    },
    [navigator, user]
  )

  const trackNewConsultation = useCallback(
    async (consultationId: string) => {
      const url = new URL(window.location.href)
      const conversationId = url.searchParams.get('cid') || undefined

      await sendPostMessage(PostMessageType.CONSULTATION_CREATED, { consultationId, ...(conversationId && { conversationId }) })

      if (conversationId) {
        url.searchParams.delete('cid')
        window.history.replaceState(null, '', url.toString())
      }
    },
    [sendPostMessage]
  )

  useEffect(() => {
    isInIframe.current = window.self !== window.top

    return () => {
      console.log('PostMessage - Cleaning up, aborting pending requests')
      abortControllersRef.current.forEach((controller) => {
        controller.abort()
      })
      abortControllersRef.current.clear()
    }
  }, [])

  return {
    sendPostMessage,
    trackNewConsultation
  }
}
